import React from "react";

export type BrandLogoVariant = "full" | "mark";
export type BrandLogoSize = "sm" | "md" | "lg";

export interface BrandLogoProps {
  variant?: BrandLogoVariant;
  size?: BrandLogoSize;
  className?: string;
  badgeText?: string;
  badgeVariant?: "emerald" | "cyan" | "amber";
  showWordmarkOnMobile?: boolean;
  onClick?: () => void;
}

/**
 * Reusable HP-Helix Vector Brand Logo Component
 * Renders an inline SVG mark or full wordmark with zero layout shift,
 * accessible ARIA labels, and interactive hover transitions.
 */
export const BrandLogo: React.FC<BrandLogoProps> = ({
  variant = "full",
  size = "md",
  className = "",
  badgeText,
  badgeVariant = "emerald",
  showWordmarkOnMobile = true,
  onClick,
}) => {
  // Dimension configurations
  const sizeConfig = {
    sm: {
      markDim: "w-7 h-7 sm:w-8 sm:h-8",
      fullHeight: "h-7 sm:h-8",
      textSize: "text-sm sm:text-base",
      badgeTextSize: "text-[9px]",
      badgePadding: "px-1.5 py-0.2",
    },
    md: {
      markDim: "w-8 h-8 sm:w-9 sm:h-9",
      fullHeight: "h-8 sm:h-9",
      textSize: "text-base sm:text-lg",
      badgeTextSize: "text-[9px] sm:text-[10px]",
      badgePadding: "px-1.5 py-0.5",
    },
    lg: {
      markDim: "w-10 h-10 sm:w-12 sm:h-12",
      fullHeight: "h-10 sm:h-12",
      textSize: "text-xl sm:text-2xl",
      badgeTextSize: "text-[11px]",
      badgePadding: "px-2 py-0.5",
    },
  }[size];

  // Badge color theme
  const badgeColors = {
    emerald: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    cyan: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    amber: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  }[badgeVariant];

  // Standalone Helical Play Mark SVG
  const renderMarkSvg = () => (
    <svg
      viewBox="0 0 48 48"
      className={`${sizeConfig.markDim} shrink-0 transition-transform duration-200 group-hover:scale-105`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="HP-Helix Play Symbol"
    >
      <defs>
        <linearGradient id="blBadgeBg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0F172A" />
          <stop offset="60%" stopColor="#090D16" />
          <stop offset="100%" stopColor="#04070D" />
        </linearGradient>

        <linearGradient id="blBadgeBorder" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.6" />
          <stop offset="50%" stopColor="#06B6D4" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#10B981" stopOpacity="0.1" />
        </linearGradient>

        <linearGradient id="blRibbonUpper" x1="0%" y1="0%" x2="100%" y2="80%">
          <stop offset="0%" stopColor="#34D399" />
          <stop offset="45%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#06B6D4" />
        </linearGradient>

        <linearGradient id="blRibbonLower" x1="0%" y1="100%" x2="100%" y2="20%">
          <stop offset="0%" stopColor="#06B6D4" />
          <stop offset="55%" stopColor="#14B8A6" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>

        <linearGradient id="blCorePlay" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#22D3EE" />
        </linearGradient>

        <filter id="blGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Rounded Carbon Badge Frame */}
      <rect
        x="2"
        y="2"
        width="44"
        height="44"
        rx="12"
        fill="url(#blBadgeBg)"
        stroke="url(#blBadgeBorder)"
        strokeWidth="1.5"
      />

      {/* Inner ambient pulse */}
      <circle cx="23" cy="24" r="14" fill="#10B981" opacity="0.08" />

      {/* Helical Intersecting Ribbon Play Icon */}
      <g filter="url(#blGlow)">
        {/* Lower Strand */}
        <path
          d="M 14.5 31.5 C 13.5 28.5 15.5 25.0 19.5 23.5 C 23.5 22.0 27.5 23.5 34.0 24.0 C 28.5 27.0 23.0 34.5 17.5 34.5 C 15.5 34.5 14.8 33.2 14.5 31.5 Z"
          fill="url(#blRibbonLower)"
          opacity="0.95"
        />

        {/* Upper Strand */}
        <path
          d="M 14.5 16.5 C 14.8 14.8 15.5 13.5 17.5 13.5 C 23.0 13.5 28.5 21.0 34.0 24.0 C 27.5 24.5 23.5 26.0 19.5 24.5 C 15.5 23.0 13.5 19.5 14.5 16.5 Z"
          fill="url(#blRibbonUpper)"
        />

        {/* Core Play Shape */}
        <path
          d="M 17.5 17.0 L 32.5 24.0 L 17.5 31.0 C 16.2 27.0 16.2 21.0 17.5 17.0 Z"
          fill="url(#blCorePlay)"
          opacity="0.92"
        />

        {/* Micro Strand Highlights */}
        <circle cx="16.5" cy="16.5" r="1.5" fill="#A7F3D0" />
        <circle cx="32.5" cy="24.0" r="1.6" fill="#67E8F9" />
        <circle cx="16.5" cy="31.5" r="1.5" fill="#5EEAD4" />
      </g>
    </svg>
  );

  if (variant === "mark") {
    return (
      <div
        className={`inline-flex items-center justify-center cursor-pointer group ${className}`}
        onClick={onClick}
        role="img"
        aria-label="HP-Helix Home"
      >
        {renderMarkSvg()}
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 sm:gap-2.5 cursor-pointer group select-none ${className}`}
      onClick={onClick}
      role="img"
      aria-label="HP-Helix Home"
    >
      {renderMarkSvg()}

      {/* Wordmark Section */}
      <div className={`flex items-center gap-1.5 ${showWordmarkOnMobile ? "flex" : "hidden sm:flex"}`}>
        <span
          className={`font-black text-white tracking-wider ${sizeConfig.textSize} uppercase font-sans`}
        >
          HP -{" "}
          <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
            HELIX
          </span>
        </span>

        {badgeText && (
          <span
            className={`font-mono font-bold uppercase rounded border ${sizeConfig.badgeTextSize} ${sizeConfig.badgePadding} ${badgeColors} tracking-wide transition-colors`}
          >
            {badgeText}
          </span>
        )}
      </div>
    </div>
  );
};

export default BrandLogo;
